import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronUp, ChevronDown, ArrowDownUp } from 'lucide-react';
import Pagination from './Pagination';
import { TableSkeleton, EmptyState } from './TableStates';
import { FONTSIZE } from '../../../../constants/style';

/**
 * Enhanced DataTable with Infinite Scroll / Lazy Loading Support
 *
 * Props:
 * - enableInfiniteScroll: bool (enables lazy loading instead of pagination)
 * - apiFunction: func (async function to fetch paginated data)
 * - pageSize: int (records per API call, default 50)
 * - scrollHeight: string (e.g. "600px" - fixed height with overflow)
 * - onLoadMore: func (called when loading more, receives page number)
 *
 * Example:
 * const fetchClients = async (page, limit) => {
 *   const response = await api.get('/clients', { params: { page, limit } });
 *   return response.data;
 * };
 *
 * <DataTable
 *   headers={headers}
 *   enableInfiniteScroll={true}
 *   apiFunction={fetchClients}
 *   pageSize={50}
 *   scrollHeight="600px"
 * />
 */
export default function DataTable({
  headers,
  data,
  isLoading = false,
  emptyMessage = 'No data found',
  onRowClick,
  selectable = false,
  onSelectionChange,
  serialNumber = true,
  zebra = true, // ON by default — odd rows gray, even rows white
  enablePagination = true,
  pagination,
  // NEW: Infinite Scroll Props
  enableInfiniteScroll = false,
  apiFunction,
  pageSize = 50,
  scrollHeight = null,
  onLoadMore
}) {
  const [sortConfig, setSortConfig] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState(new Set());

  // Internal Pagination
  const [internalPage, setInternalPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(25);

  // NEW: Infinite Scroll States
  const [infiniteScrollData, setInfiniteScrollData] = useState([]);
  const [currentApiPage, setCurrentApiPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // const [totalRecordsCount, setTotalRecordsCount] = useState(null);

  // Refs for scroll container and debounce
  const scrollContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastFetchRef = useRef(0); // Track last API call time

  // ── Reset to page 1 whenever the data array changes (e.g. after a search/filter) ──
  // Without this, being on page 2+ and then filtering causes an empty render
  // because the paginated slice starts past the end of the smaller filtered array.
  useEffect(() => {
    setInternalPage(1);
    // NEW: Reset infinite scroll when data changes
    if (enableInfiniteScroll) {
      setInfiniteScrollData([]);
      setCurrentApiPage(1);
      setHasMoreData(true);
      setIsLoadingMore(true); // Show loading immediately

      // Trigger initial fetch after reset
      if (apiFunction) {
        setTimeout(() => {
          fetchNextPage();
        }, 0);
      }
    }
  }, [enableInfiniteScroll, apiFunction]);

  // Column widths for resizing
  const [widths, setWidths] = useState(() => {
    const w = {};
    headers.forEach((h) => {
      if (h.maxWidth) w[h.key] = h.maxWidth;
      else if (h.minWidth) w[h.key] = h.minWidth;
      else if (h.resizable) w[h.key] = 150;
    });
    return w;
  });

  // ──────────────────────────────────────────────────────────────────────────
  // NEW: INFINITE SCROLL / LAZY LOADING HANDLERS
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Fetch next page of data from API
   * Throttled to avoid multiple rapid requests
   */
  const fetchNextPage = useCallback(async () => {
    if (isLoadingMore || !hasMoreData || !apiFunction) return;

    // Debounce: prevent multiple calls within 300ms
    const now = Date.now();
    if (now - lastFetchRef.current < 300) return;
    lastFetchRef.current = now;

    setIsLoadingMore(true);
    try {
      const nextPage = currentApiPage;
      const response = await apiFunction(nextPage, pageSize);

      // Extract data from response
      const newData = response?.data || response || [];
      // const total = response?.totalRecords || null;

      // Append new data to existing data (do NOT replace)
      setInfiniteScrollData((prev) => [...prev, ...newData]);
      // setTotalRecordsCount(total);

      // Check if there's more data
      if (newData.length < pageSize) {
        setHasMoreData(false);
      }

      // Increment page for next fetch
      setCurrentApiPage((prev) => prev + 1);

      // Call parent callback if provided
      if (onLoadMore) onLoadMore(nextPage);
    } catch (err) {
      console.log('Error fetching next page:', err);
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreData, apiFunction, currentApiPage, pageSize, onLoadMore]);

  /**
   * Handle scroll event with debounce
   * Trigger fetch when user scrolls near bottom (within 200px)
   */
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Check if scrolled near bottom (within 200px threshold)
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 200;

    if (isNearBottom && hasMoreData && !isLoadingMore) {
      // Debounce the fetch call
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        fetchNextPage();
      }, 200);
    }
  }, [fetchNextPage, hasMoreData, isLoadingMore]);

  /**
   * Set up scroll listener when infinite scroll is enabled
   */
  useEffect(() => {
    if (!enableInfiniteScroll) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [enableInfiniteScroll, handleScroll]);

  // ── Column Resize ──────────────────────────────────────────────────────────
  const handleResizeStart = (e, key, minW) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = widths[key] || minW || 80;

    const doDrag = (ev) => {
      window.requestAnimationFrame(() => {
        const newWidth = Math.max(startWidth + (ev.clientX - startX), minW || 60);
        setWidths((prev) => ({ ...prev, [key]: newWidth }));
      });
    };
    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (key, sortable) => {
    if (!sortable) return;
    setSortConfig((prev) => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return null;
      }
      return { key, direction: 'asc' };
    });
  };

  // ── Sorted data ───────────────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    // NEW: Use infiniteScrollData when infinite scroll is enabled
    const sourceData = enableInfiniteScroll ? infiniteScrollData : data;
    const items = [...sourceData];
    if (!sortConfig) return items;
    items.sort((a, b) => {
      let aVal = a[sortConfig.key] ?? '';
      let bVal = b[sortConfig.key] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      const aStr = String(aVal);
      const bStr = String(bVal);
      const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data, sortConfig, enableInfiniteScroll, infiniteScrollData]);

  // ── Paginated slice ───────────────────────────────────────────────────────
  // NEW: When infinite scroll is enabled, show all loaded data (no pagination)
  const finalRenderedData = useMemo(() => {
    if (enableInfiniteScroll) return sortedData; // No pagination for infinite scroll
    if (pagination) return sortedData;
    if (enablePagination) {
      const start = (internalPage - 1) * internalPageSize;
      return sortedData.slice(start, start + internalPageSize);
    }
    return sortedData;
  }, [
    sortedData,
    pagination,
    enablePagination,
    internalPage,
    internalPageSize,
    enableInfiniteScroll
  ]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIndices(new Set(finalRenderedData.map((_, i) => i)));
    } else {
      setSelectedIndices(new Set());
    }
  };
  const handleSelectRow = (index) => {
    const next = new Set(selectedIndices);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelectedIndices(next);
  };
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(finalRenderedData.filter((_, i) => selectedIndices.has(i)));
    }
  }, [selectedIndices, finalRenderedData]);

  // ── Align helper ──────────────────────────────────────────────────────────
  const getAlign = (align) => {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-end';
    return 'text-start';
  };

  const isAllSelected =
    finalRenderedData.length > 0 && selectedIndices.size === finalRenderedData.length;
  const isIndeterminate =
    selectedIndices.size > 0 && selectedIndices.size < finalRenderedData.length;

  // ── Zebra row background ───────────────────────────────────────────────────
  const rowBg = (rowIndex, isSelected) => {
    if (isSelected) return '#e8f5e9'; // selected: light green tint
    return rowIndex % 2 === 0 ? '#ffffff' : '#f7f8fa'; // even = white, odd = light gray
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        background: '#fff'
      }}
    >
      {/* ── Table wrapper: overflow-x here so sticky thead works on page scroll ── */}
      <div
        ref={enableInfiniteScroll ? scrollContainerRef : null}
        style={{
          overflowX: 'auto',
          overflowY: enableInfiniteScroll ? 'auto' : 'visible',
          width: '100%',
          WebkitOverflowScrolling: 'touch',
          // NEW: Apply fixed height when infinite scroll is enabled
          ...(enableInfiniteScroll && scrollHeight && { height: scrollHeight })
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: FONTSIZE,
            tableLayout: 'auto',
            minWidth: '100%',
            position: 'relative' // Ensure proper positioning context for sticky thead
          }}
        >
          {/* ── Column width declarations (ensures full-width even when empty) ── */}
          <colgroup>
            {selectable && <col style={{ width: '50px', minWidth: '50px' }} />}
            {serialNumber && <col style={{ width: '60px', minWidth: '60px' }} />}
            {headers.map((h) => (
              <col
                key={h.key}
                style={{
                  width: widths[h.key] ? `${widths[h.key]}px` : 'auto',
                  minWidth: `${h.minWidth || 80}px`
                }}
              />
            ))}
          </colgroup>
          {/* ── THEAD (Header row) ── */}
          <thead>
            <tr
              style={
                {
                  // background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)", // Moved to thead for sticky
                }
              }
            >
              {selectable && (
                <th
                  className="dt-sticky-th"
                  style={{
                    position: enableInfiniteScroll ? 'sticky' : 'static',
                    top: enableInfiniteScroll ? 0 : 'auto',
                    zIndex: enableInfiniteScroll ? 3 : 'auto',
                    width: 50,
                    padding: '6px 8px',
                    textAlign: 'center',
                    background: enableInfiniteScroll
                      ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                      : 'transparent'
                  }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    style={{ borderColor: '#94a3b8', cursor: 'pointer' }}
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={handleSelectAll}
                  />
                </th>
              )}

              {serialNumber && (
                <th
                  className="dt-sticky-th"
                  style={{
                    position: enableInfiniteScroll ? 'sticky' : 'static',
                    top: enableInfiniteScroll ? 0 : 'auto',
                    zIndex: enableInfiniteScroll ? 3 : 'auto',
                    width: 60,
                    padding: '6px 8px',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: FONTSIZE,
                    letterSpacing: '0.06em',
                    color: '#e2e8f0',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                  }}
                >
                  S.No
                </th>
              )}

              {headers.map((h) => {
                const isActive = sortConfig?.key === h.key;
                const wStyle = widths[h.key]
                  ? { width: widths[h.key], minWidth: widths[h.key] }
                  : {};
                return (
                  <th
                    key={h.key}
                    className="dt-sticky-th"
                    onClick={() => handleSort(h.key, h.sortable)}
                    style={{
                      ...wStyle,
                      position: enableInfiniteScroll ? 'sticky' : 'relative',
                      top: enableInfiniteScroll ? 0 : 'auto',
                      zIndex: enableInfiniteScroll ? 2 : 'auto',
                      padding: '6px 8px',
                      fontWeight: 600,
                      fontSize: FONTSIZE,
                      letterSpacing: '0.06em',
                      // textTransform: "uppercase",
                      color: isActive ? '#4ade80' : '#e2e8f0',
                      borderRight: '1px solid rgba(255,255,255,0.08)',
                      cursor: h.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.15s',
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {h.label}
                      </span>
                      {h.sortable && (
                        <span
                          style={{
                            flexShrink: 0,
                            display: 'flex',
                            opacity: isActive ? 1 : 0.45
                          }}
                        >
                          {isActive ? (
                            sortConfig.direction === 'asc' ? (
                              <ChevronUp size={14} style={{ color: '#4ade80' }} />
                            ) : (
                              <ChevronDown size={14} style={{ color: '#4ade80' }} />
                            )
                          ) : (
                            <ArrowDownUp size={13} />
                          )}
                        </span>
                      )}
                    </div>

                    {/* Resize handle */}
                    {h.resizable && (
                      <div
                        onMouseDown={(e) => handleResizeStart(e, h.key, h.minWidth || 60)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '6px',
                          cursor: 'col-resize',
                          zIndex: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            width: '2px',
                            height: '55%',
                            background: 'rgba(255,255,255,0.3)',
                            borderRadius: '2px',
                            transition: 'background 0.15s'
                          }}
                        />
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>

            {/* thin accent line under header */}
            <tr>
              <td
                colSpan={headers.length + (selectable ? 1 : 0) + (serialNumber ? 1 : 0)}
                className="dt-accent-td"
                style={{
                  padding: 0,
                  height: '3px',
                  background: 'var(--primary, #36c95f)'
                }}
              />
            </tr>
          </thead>

          {/* ── TBODY ── */}
          <tbody>
            {isLoading ||
            (enableInfiniteScroll && isLoadingMore && infiniteScrollData.length === 0) ? (
              <TableSkeleton
                columns={headers.length + (selectable ? 1 : 0) + (serialNumber ? 1 : 0)}
              />
            ) : finalRenderedData.length === 0 ? (
              <EmptyState message={emptyMessage} />
            ) : (
              finalRenderedData.map((row, rowIndex) => {
                const isSelected = selectedIndices.has(rowIndex);
                return (
                  <tr
                    key={rowIndex}
                    onDoubleClick={() => onRowClick && onRowClick(row, rowIndex)}
                    style={{
                      backgroundColor: rowBg(rowIndex, isSelected),
                      cursor: onRowClick ? 'pointer' : 'default',
                      borderBottom: '1px solid',
                      transition: 'background-color 0.12s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#eef2ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = rowBg(rowIndex, isSelected);
                    }}
                  >
                    {selectable && (
                      <td
                        style={{
                          width: 50,
                          padding: '12px 16px',
                          textAlign: 'center',
                          borderRight: '1px solid #e2e8f0'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          style={{ cursor: 'pointer' }}
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowIndex)}
                        />
                      </td>
                    )}

                    {serialNumber && (
                      <td
                        style={{
                          width: 60,
                          padding: '3px 6px',
                          textAlign: 'center',
                          fontSize: FONTSIZE,
                          color: '#64748b',
                          borderBottom: '1px solid #e8ecf0',
                          fontWeight: 500,
                          borderRight: '1px solid #e2e8f0'
                        }}
                      >
                        {((pagination?.currentPage || internalPage) - 1) *
                          (pagination?.pageSize || internalPageSize) +
                          rowIndex +
                          1}
                      </td>
                    )}

                    {headers.map((h) => {
                      const val = row[h.key];
                      return (
                        <td
                          key={h.key}
                          className={getAlign(h.align)}
                          style={{
                            padding: '3px 6px',
                            fontSize: FONTSIZE,
                            color: '#374151',
                            fontWeight: 400,
                            borderBottom: '1px solid #e8ecf0',
                            borderRight: '1px solid #e2e8f0',
                            ...(h.maxWidth
                              ? {}
                              : {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                })
                          }}
                        >
                          {h.maxWidth ? (
                            <div
                              title={
                                typeof val === 'string' || typeof val === 'number'
                                  ? String(val)
                                  : undefined
                              }
                              style={{
                                whiteSpace: 'normal',
                                display: '-webkit-box',
                                WebkitLineClamp: h.maxLines || 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word'
                              }}
                            >
                              {h.render ? h.render(val, row, rowIndex) : val}
                            </div>
                          ) : h.render ? (
                            h.render(val, row, rowIndex)
                          ) : (
                            val
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}

            {/* NEW: Loading indicator for infinite scroll */}
            {enableInfiniteScroll && isLoadingMore && (
              <tr>
                <td
                  colSpan={headers.length + (selectable ? 1 : 0) + (serialNumber ? 1 : 0)}
                  style={{
                    padding: '20px 16px',
                    textAlign: 'center',
                    background: '#f9fafb'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        border: '3px solid #e2e8f0',
                        borderTop: '3px solid #36c95f',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}
                    />
                    <span style={{ fontSize: FONTSIZE, color: '#64748b' }}>
                      Loading more records...
                    </span>
                  </div>
                  <style>{`
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                  `}</style>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* NEW: "No more records" message for infinite scroll */}
      {enableInfiniteScroll && !isLoadingMore && !hasMoreData && infiniteScrollData.length > 0 && (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            background: '#f0fdf4',
            borderTop: '1px solid #dcfce7',
            color: '#15803d',
            fontSize: FONTSIZE,
            fontWeight: 500
          }}
        >
          ✓ No more records to load
        </div>
      )}

      {/* ── PAGINATION (only show when NOT using infinite scroll) ── */}
      {!enableInfiniteScroll && enablePagination && sortedData.length > 0 && !isLoading && (
        <Pagination
          currentPage={pagination?.currentPage || internalPage}
          pageSize={pagination?.pageSize || internalPageSize}
          totalRecords={pagination?.totalRecords || sortedData.length}
          onPageChange={pagination?.onPageChange || ((page) => setInternalPage(page))}
          onPageSizeChange={
            pagination?.onPageSizeChange ||
            ((size) => {
              setInternalPageSize(size);
              setInternalPage(1);
            })
          }
        />
      )}
    </div>
  );
}
